import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardAccount } from '@webapp/modules/accounts-payable/entities/CreditCardAccount'
import {
  StatementAttachmentTypes,
  type StatementAttachmentType,
} from '@webapp/modules/accounts-payable/entities/enums/StatementAttachmentTypeEnum'
import messages from './messages'
import { useMutateStartReconciliationSession } from '../../models'
import { type ReconcileAccountFormSchema } from '../../views/ReconcileAccountForm'

// `attachment_type` derived from the uploaded file's extension (BHV-027);
// .csv/.xls go through the CSV path, .qif is QIF.
const getAttachmentType = (file: File): StatementAttachmentType =>
  /\.qif$/i.test(file.name)
    ? StatementAttachmentTypes.QIF
    : StatementAttachmentTypes.CSV

export interface IUseStartReconciliationSessionFormPresenterParams {
  account: CreditCardAccount
  onSuccess: () => void
}

export const useStartReconciliationSessionFormPresenter = ({
  account,
  onSuccess,
}: IUseStartReconciliationSessionFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { startSession, isStarting } = useMutateStartReconciliationSession()

  const handleSubmit = async (schema: ReconcileAccountFormSchema) => {
    try {
      await startSession({
        creditcardId: account.id,
        // Reuse the existing DRAFT session instead of creating one (BHV-028).
        // APPROVED sessions are closed — the account shows "Reconcile" and a
        // fresh session must be created (BHV-003).
        existingSessionId: account.hasDraftSession
          ? (account.session?.id ?? null)
          : null,
        statementDate:
          schema.currentStatementDate?.format('YYYY-MM-DD') ?? null,
        balance: schema.endingBalance || undefined,
        attachmentFile: schema.attachmentFile,
        attachmentType: schema.attachmentFile
          ? getAttachmentType(schema.attachmentFile)
          : null,
      })

      // No success toast — the FS navigation is the feedback (BHV-028).
      onSuccess()
    } catch (error) {
      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.genericErrorMessage),
      })
    }
  }

  return {
    states: {
      isStarting,
    },
    methods: {
      onSubmit: handleSubmit,
    },
  }
}
